// File: src/components/modal/TicketDetailModal.tsx

import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import { IconButton, Modal, TextField } from "@mui/material";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  TicketImageInterface,
  TicketInterface,
  TicketReplyInterface,
  UserInterface
} from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import { listTicketImages } from "../../services/ticketImage.service";
import {
  createTicketReply,
  deleteTicketReply,
  listTicketReplies,
  updateTicketReply
} from "../../services/ticketReply.service";
import ConfirmDialog from "../ConfirmDialog";
import { PriorityLevel, PriorityLevelColor, PriorityLevelLabel } from "../enum/priorityEnum";
import {
  TypeTicketStatus,
  TypeTicketStatusColor,
  TypeTicketStatusLabel
} from "../enum/typeTicketStatusEnum";
import PageToaster from "../toaster/PageToaster";
import {
  Avatar,
  Bubble,
  BubbleActions,
  BubbleCol,
  BubbleMeta,
  ChipRow,
  CloseBtn,
  Composer,
  ComposerField,
  EmptyThread,
  Header,
  HeaderMain,
  ImageGrid,
  ImageThumb,
  MetaItem,
  MetaRow,
  OriginBadge,
  Panel,
  Row,
  SendButton,
  StatusChip,
  Thread,
  TicketNumber,
  TicketTitle
} from "./TicketDetailModal.styled";

gsap.registerPlugin(useGSAP);

interface TicketDetailModalProps {
  open: boolean;
  onClose: () => void;
  ticket: TicketInterface | null;
}

/* Um avatar por pessoa, não por papel — ajuda a diferenciar quem é quem
   numa conversa com vários participantes de relance, sem precisar ler
   o nome toda vez. "Eu" sempre uso o gradiente da marca, fixo. */
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #2b7bff, #22d3ee)",
  "linear-gradient(135deg, #fb7185, #f59e0b)",
  "linear-gradient(135deg, #34d399, #22d3ee)",
  "linear-gradient(135deg, #fbbf24, #fb7185)",
  "linear-gradient(135deg, #3b82f6, #a78bfa)"
];

const gradientForUser = (id?: number | null) =>
  AVATAR_GRADIENTS[Math.abs(id ?? 0) % AVATAR_GRADIENTS.length];

const getInitials = (name?: string | null) => {
  if (!name) return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
  ).toUpperCase();
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "—";

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
  open,
  onClose,
  ticket
}) => {
  const [images, setImages] = useState<TicketImageInterface[]>([]);
  const [replies, setReplies] = useState<TicketReplyInterface[]>([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState<TicketReplyInterface | null>(
    null
  );

  const { user_id, isAdmin } = useAuth();
  const repliesEndRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const animatedIdsRef = useRef<Set<string>>(new Set());

  const containerId = "toaster-ticket-detail-modal";
  const { autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchExtras = useCallback(async (ticketId: number) => {
    try {
      const [imgs, reps] = await Promise.all([
        listTicketImages(ticketId),
        listTicketReplies(ticketId)
      ]);
      setImages(imgs);
      setReplies(reps);
    } catch (error) {
      console.error("Erro ao carregar detalhes do ticket:", error);
      handleHttpError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Ticket novo (ou modal reaberto): a conversa recomeça do zero, então a
    // entrada por mensagem também deve — sem isso, a primeira bolha do
    // próximo ticket herdaria o "já animei isso" do ticket anterior.
    animatedIdsRef.current = new Set();

    if (open && ticket?.ticket_id) {
      fetchExtras(ticket.ticket_id);
    } else {
      setImages([]);
      setReplies([]);
      setReplyText("");
    }
    setEditingReplyId(null);
    setEditText("");
    setReplyToDelete(null);
  }, [open, ticket, fetchExtras]);

  useEffect(() => {
    repliesEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [replies]);

  // Cada bolha entra com um leve fade + subida — só opacidade e posição,
  // nunca escala (escalar a partir do centro desloca conteúdo que não é
  // centralizado, como já aconteceu com os ícones da sidebar). Uma bolha
  // só anima na primeira vez que aparece: a lista inteira não "pisca" de
  // novo a cada mensagem nova.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const all = threadRef.current
          ? Array.from(
              threadRef.current.querySelectorAll<HTMLElement>("[data-msg-id]")
            )
          : [];
        const fresh = all.filter(
          (el) => !animatedIdsRef.current.has(el.dataset.msgId as string)
        );
        if (!fresh.length) return;

        fresh.forEach((el) =>
          animatedIdsRef.current.add(el.dataset.msgId as string)
        );

        gsap.fromTo(
          fresh,
          { opacity: 0, y: 10 },
          {
            opacity: 1,
            y: 0,
            duration: 0.35,
            ease: "power2.out",
            stagger: 0.05,
            clearProps: "opacity,transform"
          }
        );
      });

      return () => mm.revert();
    },
    { scope: threadRef, dependencies: [replies, images] }
  );

  const handleSendReply = async () => {
    const message = replyText.trim();
    if (!message || !ticket?.ticket_id) return;

    setSending(true);
    try {
      const created = await createTicketReply(ticket.ticket_id, message);
      setReplies((prev) => [...prev, created]);
      setReplyText("");
    } catch (error) {
      console.error("Erro ao enviar resposta do ticket:", error);
      handleHttpError(error);
    } finally {
      setSending(false);
    }
  };

  const startEdit = (reply: TicketReplyInterface) => {
    setEditingReplyId(reply.ticket_reply_id);
    setEditText(reply.message);
  };

  const cancelEdit = () => {
    setEditingReplyId(null);
    setEditText("");
  };

  const saveEdit = async () => {
    const message = editText.trim();
    if (!message || !ticket?.ticket_id || editingReplyId === null) return;

    setSavingEdit(true);
    try {
      const updated = await updateTicketReply(
        ticket.ticket_id,
        editingReplyId,
        message
      );
      setReplies((prev) =>
        prev.map((r) =>
          r.ticket_reply_id === updated.ticket_reply_id ? updated : r
        )
      );
      cancelEdit();
    } catch (error) {
      console.error("Erro ao editar resposta do ticket:", error);
      handleHttpError(error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDeleteReply = async () => {
    if (!ticket?.ticket_id || !replyToDelete) return;
    try {
      await deleteTicketReply(ticket.ticket_id, replyToDelete.ticket_reply_id);
      setReplies((prev) =>
        prev.filter((r) => r.ticket_reply_id !== replyToDelete.ticket_reply_id)
      );
    } catch (error) {
      console.error("Erro ao excluir resposta do ticket:", error);
      handleHttpError(error);
    } finally {
      setReplyToDelete(null);
    }
  };

  if (!ticket) return null;

  const status = ticket.ticket_status ?? TypeTicketStatus.NONE;
  const priority = (ticket.ticket_priority as PriorityLevel) ?? PriorityLevel.NONE;

  const renderBubble = (opts: {
    id: string;
    mine: boolean;
    author?: UserInterface | null;
    message: string;
    createdAt?: string | null;
    edited?: boolean;
    isOriginal?: boolean;
    images?: TicketImageInterface[];
    onEdit?: () => void;
    onDelete?: () => void;
    editing?: boolean;
  }) => {
    const {
      id,
      mine,
      author,
      message,
      createdAt,
      edited,
      isOriginal,
      images: bubbleImages,
      onEdit,
      onDelete,
      editing
    } = opts;

    return (
      <Row key={id} data-msg-id={id} $mine={mine}>
        <Avatar $gradient={mine ? "var(--gradient-brand)" : gradientForUser(author?.user_id)}>
          {getInitials(author?.user_name)}
        </Avatar>

        <BubbleCol $mine={mine}>
          <BubbleMeta $mine={mine}>
            <span>{author?.user_name ?? "—"}</span>
            <span>·</span>
            <span>{formatDate(createdAt)}</span>
            {isOriginal && <OriginBadge>Abriu o ticket</OriginBadge>}
            {edited && <span className="edited">(editado)</span>}
          </BubbleMeta>

          <Bubble $mine={mine}>
            {(onEdit || onDelete) && !editing && (
              <BubbleActions className="bubble-actions">
                {onEdit && (
                  <button type="button" onClick={onEdit} aria-label="Editar">
                    <EditIcon />
                  </button>
                )}
                {onDelete && (
                  <button type="button" onClick={onDelete} aria-label="Excluir">
                    <DeleteIcon />
                  </button>
                )}
              </BubbleActions>
            )}

            {editing ? (
              <ReplyEditForm
                value={editText}
                onChange={setEditText}
                onCancel={cancelEdit}
                onSave={saveEdit}
                saving={savingEdit}
              />
            ) : (
              message
            )}
          </Bubble>

          {!!bubbleImages?.length && (
            <ImageGrid>
              {bubbleImages.map((img) => (
                <ImageThumb
                  key={img.ticket_image_id}
                  href={img.url ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={img.url ?? ""} alt={img.original_name ?? "print"} />
                  <span className="zoom-hint">
                    <ZoomInRoundedIcon fontSize="inherit" />
                  </span>
                </ImageThumb>
              ))}
            </ImageGrid>
          )}
        </BubbleCol>
      </Row>
    );
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(94vw, 680px)",
            maxHeight: "88vh"
          }}
        >
          <Panel>
            <Header>
              <HeaderMain>
                <TicketNumber>Nº {ticket.ticket_number}</TicketNumber>
                <TicketTitle>{ticket.ticket_title}</TicketTitle>

                <ChipRow>
                  <StatusChip $color={TypeTicketStatusColor[status]}>
                    {TypeTicketStatusLabel[status]}
                  </StatusChip>
                  {priority !== PriorityLevel.NONE && (
                    <StatusChip $color={PriorityLevelColor[priority]}>
                      {PriorityLevelLabel[priority]}
                    </StatusChip>
                  )}
                </ChipRow>

                <MetaRow>
                  <MetaItem>
                    <PersonRoundedIcon fontSize="inherit" />
                    <strong>{ticket.user?.user_name ?? "—"}</strong>
                  </MetaItem>
                  <MetaItem>
                    <ApartmentRoundedIcon fontSize="inherit" />
                    {ticket.company?.company_name ?? "—"}
                  </MetaItem>
                  <MetaItem>
                    <CalendarTodayRoundedIcon fontSize="inherit" />
                    Aberto em {formatDate(ticket.created_at)}
                  </MetaItem>
                  {ticket.resolvedBy?.user_name && (
                    <MetaItem>
                      <TaskAltRoundedIcon fontSize="inherit" />
                      Resolvido por <strong>{ticket.resolvedBy.user_name}</strong>
                    </MetaItem>
                  )}
                </MetaRow>
              </HeaderMain>

              <CloseBtn onClick={onClose} aria-label="Fechar">
                <CloseIcon fontSize="inherit" />
              </CloseBtn>
            </Header>

            <Thread ref={threadRef}>
              {renderBubble({
                id: "original",
                mine: ticket.user?.user_id === Number(user_id),
                author: ticket.user,
                message: ticket.ticket_message,
                createdAt: ticket.created_at,
                isOriginal: true,
                images
              })}

              {replies.length === 0 ? (
                <EmptyThread>Ninguém respondeu ainda.</EmptyThread>
              ) : (
                replies.map((reply) => {
                  const mine = reply.user?.user_id === Number(user_id);
                  const editing = editingReplyId === reply.ticket_reply_id;
                  return renderBubble({
                    id: String(reply.ticket_reply_id),
                    mine,
                    author: reply.user,
                    message: reply.message,
                    createdAt: reply.created_at,
                    edited: !!reply.updated_at,
                    editing,
                    onEdit: mine ? () => startEdit(reply) : undefined,
                    onDelete:
                      mine || isAdmin ? () => setReplyToDelete(reply) : undefined
                  });
                })
              )}
              <div ref={repliesEndRef} />
            </Thread>

            <Composer>
              <ComposerField>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Escreva uma resposta..."
                  value={replyText}
                  multiline
                  maxRows={4}
                  InputProps={{ disableUnderline: true }}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
              </ComposerField>
              <SendButton
                type="button"
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                aria-label="Enviar resposta"
              >
                <SendRoundedIcon fontSize="inherit" />
              </SendButton>
            </Composer>
          </Panel>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!replyToDelete}
        title="Excluir Mensagem"
        description="Esta mensagem será apagada de vez. Deseja continuar?"
        confirmText="Excluir"
        onClose={() => setReplyToDelete(null)}
        onConfirm={handleConfirmDeleteReply}
      />
    </>
  );
};

/* Pequeno formulário inline de edição — vive dentro da própria bolha
   pra manter o gesto de "editar essa mensagem" no lugar onde ela está. */
const ReplyEditForm: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}> = ({ value, onChange, onCancel, onSave, saving }) => (
  <div>
    <TextField
      fullWidth
      size="small"
      multiline
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        "& .MuiInputBase-root": {
          bgcolor: "rgba(255,255,255,0.15)",
          color: "inherit"
        }
      }}
    />
    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", marginTop: 6 }}>
      <IconButton size="small" onClick={onCancel} sx={{ color: "inherit" }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={onSave}
        disabled={saving || !value.trim()}
        sx={{ color: "inherit" }}
      >
        <CheckIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </div>
  </div>
);

export default TicketDetailModal;
